import React from "react";
import { motion } from "framer-motion";
import { Download, Smartphone, Sparkles, Users, MessageCircle, FlaskConical, Gauge, Workflow } from "lucide-react";
import { goToAppStore } from "../lib/api";

const STEPS = [
  { icon: Download, title: "Download the app", desc: "Get Senior Stylist free from the Apple App Store or Google Play.", img: "https://static.prod-images.emergentagent.com/jobs/ca9230b3-f03c-418c-bfeb-5a9295ae9f53/images/e5f2867e7572859f5326f3df4d8686abeb08aadabf0cb2f932a0383f4b231842.jpeg" },
  { icon: Smartphone, title: "Sign in with your phone", desc: "Create your account in seconds with a quick phone verification.", img: "https://static.prod-images.emergentagent.com/jobs/ca9230b3-f03c-418c-bfeb-5a9295ae9f53/images/ac49e499094512fea79609b921fd57573050bac8ed42022da53603ed43245a1b.jpeg" },
  { icon: Sparkles, title: "Complete your Style DNA", desc: "Answer 15 questions about your hair, scalp and goals.", img: "https://static.prod-images.emergentagent.com/jobs/ca9230b3-f03c-418c-bfeb-5a9295ae9f53/images/866dda301647efab8c8df10ff9bf1583d179bb7f35e536f2add7585fb94cc851.jpeg" },
  { icon: Users, title: "Get matched", desc: "We suggest your five closest certified stylists, pick your favourite.", img: "https://static.prod-images.emergentagent.com/jobs/ca9230b3-f03c-418c-bfeb-5a9295ae9f53/images/a10f427b88ab529d2799dc796ee90d1f4eacfde6f980a43f0d46e22e68d7a7e5.jpeg" },
  { icon: MessageCircle, title: "Start messaging", desc: "Chat with your stylist for personalised, expert guidance.", img: "https://static.prod-images.emergentagent.com/jobs/ca9230b3-f03c-418c-bfeb-5a9295ae9f53/images/6d5385a684c2f1c229bac6bc3c6fa3315148bbcc606fa1936d9b3643a61a4255.jpeg" },
];

const ENGINE_STEPS = [
  {
    n: "01",
    icon: FlaskConical,
    title: "Every client starts with a Style DNA quiz",
    body: "Before anyone can book, each client answers 15 quick questions about their hair, texture, scalp and past colour, and adds photos plus the products they already use. This builds a full hair profile, so you know exactly what you are working with and clients are matched to the right stylist from the very start.",
  },
  {
    n: "02",
    icon: Gauge,
    title: "Only 115 clients per stylist",
    body: "Other apps pack in as many stylists as they can, which means quiet weeks and constant discounting. We give each verified senior stylist a maximum of 115 clients. Once you are full, new clients join a waitlist until more space opens up. This keeps your books steady, and the earning potential is real: at Tier 3, six consultations a day, just one hour out of your day, works out at £1,332 a month from remote consultations alone.",
  },
  {
    n: "03",
    icon: Workflow,
    title: "Matched by hair type, paid on repeat",
    body: "Clients are matched to you by their hair type and needs, not just who happens to be free. Each consultation lasts a maximum of 10 minutes, most just a few minutes. You then recommend a simple home routine they can buy straight through the app, so you earn ongoing commission without holding any stock.",
  },
];

export const HowItWorks = () => {
  return (
    <section id="how" className="py-16 md:py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <span className="eyebrow">Simple from the start</span>
          <h2 className="mt-3 font-serif text-3xl sm:text-4xl lg:text-5xl text-stylist-ink">
            How it works
          </h2>
          <p className="mt-4 text-stylist-ink2">
            Expert hair guidance in five easy steps, no salon appointment needed.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-6">
          {STEPS.map((s, i) => (
            <motion.div
              key={s.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              className="relative bg-white border border-stylist-border rounded-lg p-5 hover:border-stylist-gold/40 hover:shadow-xl transition-all duration-500"
              data-testid={`how-step-${i + 1}`}
            >
              <div className="rounded-[1.5rem] bg-stylist-ink p-1.5 shadow-lg" data-testid={`how-step-${i + 1}-frame`}>
                <div className="aspect-[848/1264] w-full overflow-hidden rounded-[1.1rem] bg-stylist-cream">
                  <img
                    src={s.img}
                    alt={`${s.title} screen in the Senior Stylist app`}
                    loading="lazy"
                    className="w-full h-full object-contain"
                    data-testid={`how-step-${i + 1}-image`}
                  />
                </div>
              </div>
              <div className="mt-5 flex items-center gap-3">
                <span className="font-mono text-xs tracking-[0.3em] text-stylist-muted">
                  0{i + 1}
                </span>
                <div className="w-8 h-8 rounded-full bg-stylist-gold/15 flex items-center justify-center">
                  <s.icon className="w-4 h-4 text-stylist-gold" />
                </div>
              </div>
              <h3 className="mt-3 font-serif text-xl text-stylist-ink leading-snug">{s.title}</h3>
              <p className="mt-2 text-sm text-stylist-ink2 leading-relaxed">{s.desc}</p>
            </motion.div>
          ))}
        </div>

        {/* PLATFORM ENGINE & EQUILIBRIUM */}
        <div
          className="mt-16 relative overflow-hidden rounded-[2rem] bg-stylist-ink text-white px-6 sm:px-10 lg:px-16 py-14 lg:py-20"
          data-testid="platform-engine"
        >
          <div
            className="pointer-events-none absolute -top-24 -right-24 w-96 h-96 rounded-full opacity-30 blur-3xl"
            style={{ background: "radial-gradient(circle, rgba(198,161,91,0.55), transparent 70%)" }}
          />
          <div className="relative max-w-3xl">
            <span className="font-mono text-[11px] uppercase tracking-[0.32em] text-stylist-gold">
              How the platform works
            </span>
            <h3 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl leading-tight">
              Built with intentional limits, we protect{" "}
              <span className="text-stylist-gold">your market, your chair, and your earnings.</span>
            </h3>
            <p className="mt-5 text-white/70 text-base leading-relaxed">
              Most booking apps sign up too many stylists, so everyone ends up fighting for the
              same clients. We do the opposite. We cap how many clients each stylist takes on, so
              your income stays steady and every client gets proper, tailored hair advice.
            </p>

            <div
              className="mt-8 inline-flex flex-col sm:flex-row sm:items-center gap-5 rounded-2xl border border-stylist-gold/30 bg-stylist-gold/[0.08] px-7 py-5"
              data-testid="earnings-highlight"
            >
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-stylist-gold/80">
                  Potential earnings
                </p>
                <p className="mt-1 font-serif text-4xl sm:text-5xl text-stylist-gold leading-none">
                  £1,332<span className="text-2xl sm:text-3xl">/mo</span>
                </p>
              </div>
              <div className="hidden sm:block h-12 w-px bg-white/15" />
              <p className="max-w-xs text-sm text-white/60 leading-relaxed">
                At Tier 3, based on 6 consultations a day across 30 days, roughly one hour out of
                your day. You still put the work in, but the upside is there from the start.
              </p>
            </div>
          </div>

          <div className="relative mt-12 grid gap-6 md:grid-cols-3">
            {ENGINE_STEPS.map((s, i) => (
              <motion.div
                key={s.n}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="rounded-2xl border border-white/10 bg-white/[0.04] p-7 hover:border-stylist-gold/40 transition-colors duration-500"
                data-testid={`engine-step-${s.n}`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-mono text-xs tracking-[0.3em] text-stylist-gold/80">{s.n}</span>
                  <div className="w-11 h-11 rounded-full bg-stylist-gold/15 flex items-center justify-center">
                    <s.icon className="w-5 h-5 text-stylist-gold" />
                  </div>
                </div>
                <h4 className="mt-5 font-serif text-xl leading-snug">{s.title}</h4>
                <p className="mt-3 text-sm text-white/65 leading-relaxed">{s.body}</p>
              </motion.div>
            ))}
          </div>

          <div className="relative mt-12 border-t border-white/10 pt-8">
            <p className="font-mono text-[11px] uppercase tracking-[0.32em] text-stylist-gold">
              Built for quality. Capped for capacity.
            </p>
            <p className="mt-4 max-w-3xl text-white/70 text-base leading-relaxed">
              By keeping it to 115 clients per stylist, we protect your time, stop the app from
              getting overcrowded, and make sure every client gets real, expert advice.
            </p>
          </div>
        </div>

        <div className="mt-12 text-center">
          <button
            onClick={goToAppStore}
            data-testid="how-cta-subscribe"
            className="inline-flex items-center justify-center gap-2 rounded-full bg-stylist-ink text-white px-8 py-4 text-sm hover:bg-stylist-gold transition-colors duration-300"
          >
            Subscribe today
          </button>
        </div>
      </div>
    </section>
  );
};
